import { useState } from "react";
import { useNotify, useRefresh, useTranslate } from "ra-core";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { LEAD_TYPE_CHOICES } from "./leadConsts";

interface ConvertLeadDialogProps {
  lead: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ConvertLeadDialog = ({
  lead,
  open,
  onOpenChange,
}: ConvertLeadDialogProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const translate = useTranslate();

  const [accountName, setAccountName] = useState(
    lead?.account_name ||
      [lead?.first_name, lead?.last_name].filter(Boolean).join(" "),
  );
  const [accountType, setAccountType] = useState(lead?.lead_type ?? "Customer");
  const [firstName, setFirstName] = useState(lead?.first_name ?? "");
  const [lastName, setLastName] = useState(lead?.last_name ?? "");
  const [converting, setConverting] = useState(false);

  const handleConvert = async () => {
    setConverting(true);
    try {
      const { data, error } = await getSupabaseClient().functions.invoke(
        "convert_lead",
        {
          body: {
            lead_id: lead.id,
            company_name: accountName,
            account_type: accountType,
            contact: { first_name: firstName, last_name: lastName },
          },
        },
      );
      if (error) throw error;
      notify(
        translate("resources.leads.converted", {
          _: "Lead converted to account and contact",
        }),
        { type: "success" },
      );
      onOpenChange(false);
      refresh();
      if (data?.company_id) navigate(`/companies/${data.company_id}/show`);
    } catch (err: any) {
      notify(err?.message ?? "Conversion failed", { type: "error" });
    } finally {
      setConverting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {translate("resources.leads.convert.title", {
              _: "Convert lead",
            })}
          </DialogTitle>
          <DialogDescription>
            {translate("resources.leads.convert.description", {
              _: "Creates a new Account (status: Lead-Account) and Contact from this lead.",
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="convert-account-name">
              {translate("resources.leads.fields.account_name", {
                _: "Account name",
              })}
            </Label>
            <Input
              id="convert-account-name"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label>
              {translate("resources.leads.fields.account_type", {
                _: "Account type",
              })}
            </Label>
            <Select value={accountType} onValueChange={setAccountType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LEAD_TYPE_CHOICES.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="convert-first-name">
                {translate("resources.leads.fields.first_name", {
                  _: "First name",
                })}
              </Label>
              <Input
                id="convert-first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="convert-last-name">
                {translate("resources.leads.fields.last_name", {
                  _: "Last name",
                })}
              </Label>
              <Input
                id="convert-last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {translate("ra.action.cancel")}
          </Button>
          <Button
            onClick={handleConvert}
            disabled={converting || !accountName.trim()}
          >
            {converting
              ? translate("resources.leads.convert.converting", {
                  _: "Converting…",
                })
              : translate("resources.leads.convert.confirm", { _: "Convert" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
